import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditHistory as CreditHistoryType, CreditOperationType, CreditType, User } from '@/types/model';
import { TableColumn } from '@/types';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import { useCreditHistoryData } from '@/hooks/useCreditHistory';
import { useUser, useUsers } from '@/hooks';
import { useBranding } from '@/context/BrandingContext';
import { formatDateTime } from '@/utils/formatters';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';
import Button from '@/components/ui/Button';
import { FileText, TrendingDown, TrendingUp } from 'lucide-react';

const formatUserOptionLabel = (user: Pick<User, 'username' | 'fullName'>) =>
  user.fullName?.trim() ? `${user.fullName} (${user.username})` : user.username;

const buildUserOptions = (users: User[], selectedUser?: User | null) => {
  const options = new Map<string, { value: string; label: string }>();

  if (selectedUser) {
    options.set(String(selectedUser.id), {
      value: String(selectedUser.id),
      label: formatUserOptionLabel(selectedUser),
    });
  }

  users.forEach((user) => {
    options.set(String(user.id), {
      value: String(user.id),
      label: formatUserOptionLabel(user),
    });
  });

  return Array.from(options.values());
};

const CreditHistory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [targetUserSearch, setTargetUserSearch] = useState('');
  const [performedBySearch, setPerformedBySearch] = useState('');
  const { branding } = useBranding();
  const reportsEnabled = branding.modules?.reports === true;

  const targetUserIdFilter = searchParams.get('targetUser.id');
  const usernameFilter = searchParams.get('username');
  const performedByUserIdFilter = searchParams.get('performedByUser.id');
  const dateFromFilter = searchParams.get('dateFrom');
  const dateToFilter = searchParams.get('dateTo');

  useEffect(() => {
    if (!searchParams.has('search')) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('search');
    nextParams.delete('_page');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const initialFilters = useMemo(() => {
    const filters: Record<string, unknown> = {};

    if (targetUserIdFilter) {
      filters['targetUser.id'] = targetUserIdFilter;
    } else if (usernameFilter) {
      filters.username = usernameFilter;
    }
    if (performedByUserIdFilter) {
      filters['performedByUser.id'] = performedByUserIdFilter;
    }
    if (dateFromFilter) {
      filters.dateFrom = dateFromFilter;
    }
    if (dateToFilter) {
      filters.dateTo = dateToFilter;
    }
    return filters;
  }, [dateFromFilter, dateToFilter, performedByUserIdFilter, targetUserIdFilter, usernameFilter]);

  const targetUserSearchTerm = targetUserSearch.trim();
  const performedBySearchTerm = performedBySearch.trim();

  const { data: targetUsersData, isLoading: isTargetUsersLoading } = useUsers({
    _size: 25,
    search: targetUserSearchTerm || undefined,
  });
  const { data: performedByUsersData, isLoading: isPerformedByUsersLoading } = useUsers({
    _size: 25,
    search: performedBySearchTerm || undefined,
  });
  const { data: selectedTargetUser, isLoading: isSelectedTargetUserLoading } = useUser(targetUserIdFilter ?? '');
  const { data: selectedPerformedByUser, isLoading: isSelectedPerformedByUserLoading } = useUser(performedByUserIdFilter ?? '');

  const targetUserOptions = useMemo(
    () => buildUserOptions(targetUsersData?.content ?? [], selectedTargetUser),
    [selectedTargetUser, targetUsersData]
  );

  const performedByOptions = useMemo(
    () => buildUserOptions(performedByUsersData?.content ?? [], selectedPerformedByUser),
    [performedByUsersData, selectedPerformedByUser]
  );

  const updateFilterParam = (key: 'targetUser.id' | 'performedByUser.id' | 'dateFrom' | 'dateTo', value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }

    if (key === 'targetUser.id') {
      nextParams.delete('username');
    }

    nextParams.delete('search');
    nextParams.delete('_page');
    setSearchParams(nextParams);
  };

  const handleViewReport = () => {
    const nextParams = new URLSearchParams();

    if (performedByUserIdFilter) {
      nextParams.set('performedByUser.id', performedByUserIdFilter);
    }
    if (dateFromFilter) {
      nextParams.set('dateFrom', dateFromFilter);
    }
    if (dateToFilter) {
      nextParams.set('dateTo', dateToFilter);
    }

    const queryString = nextParams.toString();
    navigate(`/reports/credit-sales${queryString ? `?${queryString}` : ''}`);
  };

  // Badge component for operation type
  const OperationTypeBadge = ({ operationType }: { operationType: CreditOperationType }) => {
    const isAddition =
      operationType === 'ADD_CREDITS' ||
      operationType === 'ADD_CREDITS_FOR_SALE' ||
      operationType === 'ADD_DEMO_CREDITS';
    const isRefund = operationType === 'REFUND_CREDITS' || operationType === 'REFUND_CREDITS_FOR_SALE';
    const baseClasses = "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full";

    if (isAddition) {
      const labelByOperation: Record<'ADD_CREDITS' | 'ADD_CREDITS_FOR_SALE' | 'ADD_DEMO_CREDITS', string> = {
        ADD_CREDITS: 'Agregar',
        ADD_CREDITS_FOR_SALE: 'Agregar (Venta)',
        ADD_DEMO_CREDITS: 'Agregar (Demo)',
      };
      return (
        <span className={`${baseClasses} bg-green-100 text-green-800`}>
          <TrendingUp size={12}/>
          {labelByOperation[operationType as 'ADD_CREDITS' | 'ADD_CREDITS_FOR_SALE' | 'ADD_DEMO_CREDITS']}
        </span>
      );
    } else if(isRefund) {
      return (
        <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
          <TrendingUp size={12}/>
          {operationType === 'REFUND_CREDITS' ? 'Reembolsar' : 'Reembolsar (Venta)'}
        </span>
      );
    } else {
      return (
        <span className={`${baseClasses} bg-red-100 text-red-800`}>
          <TrendingDown size={12}/>
          {operationType === 'DEDUCT_CREDITS' ? 'Quitar' : 'Quitar (Venta)'}
        </span>
      );
    }
  };

  // Badge component for credit type
  const CreditTypeBadge = ({ creditType }: { creditType: CreditType }) => {
    const baseClasses = "inline-flex items-center px-2 py-1 text-xs font-medium rounded-full";

    if (creditType === 'NORMAL') {
      return (
        <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
          Normal
        </span>
      );
    } else {
      return (
        <span className={`${baseClasses} bg-purple-100 text-purple-800`}>
          Venta
        </span>
      );
    }
  };

  const columns: TableColumn<CreditHistoryType>[] = [
    {
      key: 'operationDate',
      sortable: true,
      header: 'Fecha',
      accessor: (record: CreditHistoryType) => (
        <span className="text-sm text-gray-900">
          {formatDateTime(record.operationDate)}
        </span>
      ),
    },
    {
      key: 'targetUser',
      sortable: true,
      header: 'Usuario Objetivo',
      accessor: (record: CreditHistoryType) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{record.targetUser?.username || '(Usuario eliminado)'}</span>
          <span className="text-xs text-gray-500">{record.targetUser?.fullName || '(Usuario eliminado)'}</span>
        </div>
      ),
    },
    {
      key: 'performedByUser',
      header: 'Realizado Por',
      accessor: (record: CreditHistoryType) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{record.performedByUser.username || '(Usuario eliminado)'}</span>
          <span className="text-xs text-gray-500">{record.performedByUser.fullName || '(Usuario eliminado)'}</span>
        </div>
      ),
    },
    {
      key: 'operationType',
      header: 'Operación',
      accessor: (record: CreditHistoryType) => <OperationTypeBadge operationType={record.operationType}/>,
    },
    {
      key: 'creditType',
      header: 'Tipo Crédito',
      accessor: (record: CreditHistoryType) => <CreditTypeBadge creditType={record.creditType}/>,
    },
    {
      key: 'amount',
      header: 'Cantidad',
      accessor: (record: CreditHistoryType) => {
        const isAddition =
          record.operationType === 'ADD_CREDITS' ||
          record.operationType === 'ADD_CREDITS_FOR_SALE' ||
          record.operationType === 'ADD_DEMO_CREDITS';
        const isRefund = record.operationType === 'REFUND_CREDITS' || record.operationType === 'REFUND_CREDITS_FOR_SALE';
        return (
          <span className={`font-medium ${isAddition ? 'text-green-600' : isRefund ? 'text-yellow-600' : 'text-red-600'}`}>
            {isAddition || isRefund ? '+' : '-'}{record.amount}
          </span>
        );
      },
    },
    {
      key: 'balanceBefore',
      header: 'Balance Anterior',
      accessor: (record: CreditHistoryType) => (
        <span className="text-sm text-gray-600">{record.balanceBefore}</span>
      ),
    },
    {
      key: 'balanceAfter',
      header: 'Balance Posterior',
      accessor: (record: CreditHistoryType) => (
        <span className="text-sm font-medium text-gray-900">{record.balanceAfter}</span>
      ),
    },
    {
      key: 'reason',
      header: 'Motivo',
      accessor: (record: CreditHistoryType) => (
        <span className="text-sm text-gray-600">
          {record.reason || 'Sin especificar'}
        </span>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <DataTableWithQuery<CreditHistoryType>
          columns={columns}
          useQueryHook={useCreditHistoryData}
          tableStyle="compact"
          title="Historial de Créditos"
          showTotalCount
          initialFilters={initialFilters}
          headerRenderer={({ totalCount }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">Historial de Créditos</h3>
                    <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                      {totalCount}
                    </span>
                  </div>
                  {reportsEnabled && (
                    <Button onClick={handleViewReport} size="sm" variant="outline">
                      <FileText className="mr-2 h-4 w-4" />
                      Ver reporte
                    </Button>
                  )}
                </div>
                <div className="mt-3">
                  <AutocompleteSelect
                    value={targetUserIdFilter ?? ''}
                    onChange={(value) => {
                      updateFilterParam('targetUser.id', value);
                      setTargetUserSearch('');
                    }}
                    onSearch={setTargetUserSearch}
                    options={targetUserOptions}
                    placeholder="Usuario objetivo"
                    size="sm"
                    className="text-base"
                    allowClear
                    loading={isTargetUsersLoading || isSelectedTargetUserLoading}
                    emptyMessage="No se encontraron usuarios"
                  />
                </div>
                <div className="mt-3">
                  <AutocompleteSelect
                    value={performedByUserIdFilter ?? ''}
                    onChange={(value) => {
                      updateFilterParam('performedByUser.id', value);
                      setPerformedBySearch('');
                    }}
                    onSearch={setPerformedBySearch}
                    options={performedByOptions}
                    placeholder="Realizado por"
                    size="sm"
                    className="text-base"
                    allowClear
                    loading={isPerformedByUsersLoading || isSelectedPerformedByUserLoading}
                    emptyMessage="No se encontraron usuarios"
                  />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={dateFromFilter ?? ''}
                    onChange={(event) => updateFilterParam('dateFrom', event.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                  <input
                    type="date"
                    value={dateToFilter ?? ''}
                    onChange={(event) => updateFilterParam('dateTo', event.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <h3 className="text-lg font-medium text-gray-900">Historial de Créditos</h3>
                <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                  {totalCount}
                </span>
                <div className="min-w-[220px]">
                  <AutocompleteSelect
                    value={targetUserIdFilter ?? ''}
                    onChange={(value) => {
                      updateFilterParam('targetUser.id', value);
                      setTargetUserSearch('');
                    }}
                    onSearch={setTargetUserSearch}
                    options={targetUserOptions}
                    placeholder="Usuario objetivo"
                    size="sm"
                    className="text-base"
                    allowClear
                    loading={isTargetUsersLoading || isSelectedTargetUserLoading}
                    emptyMessage="No se encontraron usuarios"
                  />
                </div>
                <div className="min-w-[220px]">
                  <AutocompleteSelect
                    value={performedByUserIdFilter ?? ''}
                    onChange={(value) => {
                      updateFilterParam('performedByUser.id', value);
                      setPerformedBySearch('');
                    }}
                    onSearch={setPerformedBySearch}
                    options={performedByOptions}
                    placeholder="Realizado por"
                    size="sm"
                    className="text-base"
                    allowClear
                    loading={isPerformedByUsersLoading || isSelectedPerformedByUserLoading}
                    emptyMessage="No se encontraron usuarios"
                  />
                </div>
                <div className="min-w-[160px]">
                  <input
                    type="date"
                    value={dateFromFilter ?? ''}
                    onChange={(event) => updateFilterParam('dateFrom', event.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="min-w-[160px]">
                  <input
                    type="date"
                    value={dateToFilter ?? ''}
                    onChange={(event) => updateFilterParam('dateTo', event.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                {reportsEnabled && (
                  <div className="ml-auto">
                    <Button onClick={handleViewReport} size="sm" variant="outline">
                      <FileText className="mr-2 h-4 w-4" />
                      Ver reporte
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default CreditHistory;
